import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AuthLayout from "@/components/auth/AuthLayout";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/utils/api";
import { consumePostAuthRedirect, peekPostAuthRedirect } from "@/utils/postAuthRedirect";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || peekPostAuthRedirect() || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(
    new URLSearchParams(location.search).get("error") || ""
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      consumePostAuthRedirect();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your tots360 account"
      sideTitle="360° insight into your child's growth — trusted by parents, doctors & schools."
      sideBullets={[
        "AI-powered bone age, nutrition & development reports",
        "Longitudinal growth tracking with IAP-2015 percentile bands",
        "Role-based dashboards for parents, doctors & teachers",
        "Consent-first, encrypted, audited access",
      ]}
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="text-sm text-slate-300" htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="login-email-input"
            className="mt-1 w-full px-4 py-2.5 rounded-lg bg-[#0E1A3A] border border-slate-700 focus:border-sky-400 focus:ring-1 focus:ring-sky-400 text-slate-100 outline-none"
          />
        </div>
        <div>
          <label className="text-sm text-slate-300" htmlFor="login-password">Password</label>
          <div className="relative mt-1">
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              data-testid="login-password-input"
              className="w-full px-4 py-2.5 rounded-lg bg-[#0E1A3A] border border-slate-700 focus:border-sky-400 focus:ring-1 focus:ring-sky-400 text-slate-100 outline-none pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-200"
              data-testid="login-toggle-password"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2" data-testid="login-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="login-submit-button"
          className="w-full mt-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 text-white font-medium hover:opacity-95 disabled:opacity-60 transition"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <div className="flex items-center gap-3 my-6">
        <span className="flex-1 h-px bg-slate-700" />
        <span className="text-xs text-slate-500">OR</span>
        <span className="flex-1 h-px bg-slate-700" />
      </div>

      <GoogleSignInButton returnTo="/dashboard" />

      <p className="mt-8 text-sm text-slate-400 text-center">
        Don't have an account?{" "}
        <Link to="/signup" className="text-sky-400 hover:text-sky-300 font-medium" data-testid="login-to-signup-link">
          Create one
        </Link>
      </p>
    </AuthLayout>
  );
}
